/**
 * *New task list* and *Edit list* — `docs/activity.md` §8.6 (Board 19 C):
 * the 480 `Sheet`. *Name* (required) · *Members* (the maker first, *up to
 * 25*) · *Buckets* (the defaults with grips and ×; **+ Add a bucket**) ·
 * *Meets* (optional — a meeting the maker can see) · footer *Members see it
 * under Task lists* · **Cancel · Create list**.
 *
 * Aaron, Oct 23: there is no Done bucket (buckets aren't statuses — a ticked
 * task stays in its bucket, under *Completed*), so every bucket has its ×
 * while another is left; the grips drag, in *Edit list* as in *New*, and the
 * order is saved; and *Edit list* can delete the list — its tasks stay.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DEFAULT_BUCKETS, LIST_MAX_BUCKETS, LIST_MAX_MEMBERS, addDays, type ActivityPerson, type CalendarAnswer,
} from '@rowted/shared'
import { MenuSelect } from '../../components/menu-select.js'
import { ConfirmDialog } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { Face } from '../faces.js'
import { PersonPicker } from './pickers.js'
import { useSetup } from '../new/common.js'
import { NewShell, SHEET_LAYER } from '../new/kit.js'
import { usePhone } from '../../lib/use-narrow.js'
import { tasksChanged } from './task-sheet.js'
import type { TaskList } from './types.js'
import { clockWords, dayLabel } from './words.js'

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface MeetingOption { id: string; title: string; when: string }

interface BucketRow { key: string; id: string | null; name: string }

export function TaskListSheet({
  open, onClose, list, onSaved, onDeleted,
}: {
  open: boolean
  onClose: () => void
  /** Editing this list; null makes a new one. */
  list: TaskList | null
  onSaved: (list: TaskList) => void
  /** The list was deleted (softly; its tasks stay). */
  onDeleted?: (list: TaskList) => void
}) {
  const { me, today, directory } = useActivity()
  const setup = useSetup()
  const phone = usePhone()
  const [name, setName] = useState('')
  const [members, setMembers] = useState<string[]>([])
  const [buckets, setBuckets] = useState<BucketRow[]>([])
  const [meetingId, setMeetingId] = useState<string | null>(null)
  const [meetings, setMeetings] = useState<MeetingOption[]>([])
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const addAt = useRef<HTMLButtonElement>(null)
  const rowsAt = useRef(new Map<string, HTMLDivElement>())
  const [lifted, setLifted] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!open) return
    setError(null)
    setName(list?.name ?? '')
    setMembers(list ? list.memberIds : [me.user.id])
    setBuckets(list
      ? list.buckets.map((b) => ({ key: b.id, id: b.id, name: b.name }))
      : DEFAULT_BUCKETS.map((b, index) => ({ key: `new-${index}`, id: null, name: b })))
    setMeetingId(list?.meets?.meetingId ?? null)
    setDeleting(false)
  }, [open, list, me.user.id])

  /* The meetings this person can see, from their own calendar over the next four weeks. */
  useEffect(() => {
    if (!open) return
    activityRequest<CalendarAnswer>(`/activity/calendar?people=${me.user.id}&from=${today}&to=${addDays(today, 27)}`)
      .then((answer) => {
        const seen = new Map<string, MeetingOption>()
        for (const person of answer.people) {
          for (const day of person.days) {
            for (const item of day.items) {
              if (item.kind !== 'meeting' || seen.has(item.id)) continue
              const weekday = WEEKDAYS[new Date(`${item.date}T12:00:00Z`).getUTCDay()]!
              /* A custom repeat (0138) is said whole: *Every 2 weeks on Monday and Wednesday 9:00 AM*. */
              const when = item.repeatWords ? `${item.repeatWords} ${clockWords(item.start)}`
                : item.repeatRule === 'weekly' ? `${weekday}s ${clockWords(item.start)}`
                : item.repeatRule === 'every_other_week' ? `Every other ${weekday} ${clockWords(item.start)}`
                : `${dayLabel(item.date)} ${clockWords(item.start)}`
              seen.set(item.id, { id: item.id, title: item.title, when })
            }
          }
        }
        setMeetings([...seen.values()])
      })
      .catch(() => setMeetings([]))
  }, [open, me.user.id, today])

  const people = useMemo<ActivityPerson[]>(() => directory?.people ?? [], [directory])
  const byId = new Map(people.map((p) => [p.id, p]))
  const options = meetings.some((m) => m.id === list?.meets?.meetingId) || !list?.meets
    ? meetings
    : [{ id: list.meets.meetingId, title: list.meets.title, when: list.meets.atWords.replace(/^meets /, '') }, ...meetings]
  const chosenMeeting = options.find((m) => m.id === meetingId) ?? null

  /** One bucket to a new place (by its key, so a row typed into meanwhile keeps its words). */
  const moveTo = (key: string, to: number) => {
    setBuckets((current) => {
      const from = current.findIndex((row) => row.key === key)
      if (from === -1 || from === to || to < 0 || to >= current.length) return current
      const next = [...current]
      const [row] = next.splice(from, 1)
      next.splice(to, 0, row!)
      return next
    })
  }

  /**
   * A grip, pressed and moved: the row goes where the pointer passes the
   * others' middles (measured at the press, so the room it makes does not
   * move the target). Pointer events, so a finger and a pencil drag too.
   */
  const grab = (key: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    const order = buckets.map((row) => row.key)
    const from = order.indexOf(key)
    const middles = order.map((one) => {
      const box = rowsAt.current.get(one)?.getBoundingClientRect()
      return box ? box.top + box.height / 2 : 0
    })
    const target = (y: number) => middles.filter((mid, index) => index !== from && mid < y).length
    let at = from
    setLifted(key)
    const move = (next: PointerEvent) => {
      next.preventDefault()
      const to = target(next.clientY)
      if (to === at) return
      at = to
      moveTo(key, to)
    }
    const up = () => {
      setLifted(null)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  const remove = async () => {
    if (!list) return
    setBusy(true)
    setError(null)
    try {
      await activityRequest(`/task-lists/${list.id}/remove`, { method: 'POST', body: {} })
      tasksChanged()
      setDeleting(false)
      onDeleted?.(list)
      onClose()
    } catch (cause) {
      setDeleting(false)
      setError(cause instanceof Error ? cause.message : 'That list could not be deleted.')
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!name.trim()) { setError('Give the list a name.'); return }
    setBusy(true)
    setError(null)
    try {
      let saved: TaskList
      if (!list) {
        saved = await activityRequest<TaskList>('/task-lists', {
          method: 'POST',
          body: { name: name.trim(), memberIds: members, buckets: buckets.map((b) => b.name.trim()).filter(Boolean), meetingId },
        })
      } else {
        saved = await activityRequest<TaskList>(`/task-lists/${list.id}`, {
          method: 'PATCH', body: { name: name.trim(), memberIds: members, meetingId },
        })
        /* Buckets: renamed, removed and added through their own endpoints (§15). */
        for (const bucket of list.buckets) {
          const row = buckets.find((b) => b.id === bucket.id)
          if (!row) await activityRequest(`/task-buckets/${bucket.id}/remove`, { method: 'POST', body: {} })
          else if (row.name.trim() && row.name.trim() !== bucket.name) {
            await activityRequest(`/task-buckets/${bucket.id}`, { method: 'PATCH', body: { name: row.name.trim() } })
          }
        }
        const made = new Map<string, string>()
        let known = new Set(list.buckets.map((bucket) => bucket.id))
        for (const row of buckets.filter((b) => !b.id && b.name.trim())) {
          const added = await activityRequest<{ items: { id: string }[] }>(`/task-lists/${list.id}/buckets`,
            { method: 'POST', body: { name: row.name.trim() } })
          const id = added.items.find((bucket) => !known.has(bucket.id))?.id
          if (id) made.set(row.key, id)
          known = new Set(added.items.map((bucket) => bucket.id))
        }
        /* And the order the grips left them in (Aaron, Oct 23), when it is not already the server's. */
        const order = buckets.map((row) => row.id ?? made.get(row.key)).filter((id): id is string => Boolean(id))
        saved = await activityRequest<TaskList>(`/task-lists/${list.id}`)
        if (order.length === saved.buckets.length && order.some((id, index) => saved.buckets[index]?.id !== id)) {
          await activityRequest(`/task-lists/${list.id}/buckets/order`, { method: 'POST', body: { ids: order } })
          saved = await activityRequest<TaskList>(`/task-lists/${list.id}`)
        }
      }
      tasksChanged()
      onSaved(saved)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That list could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const label = 'mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted'
  const head = 'mb-1.5 flex text-[12px] font-semibold text-text-muted'

  if (!open) return null
  /* Board 19 C draws New's own shell: the kind's tile, 16/700, × and no keycap (as every New sheet, §9). */
  return (
    <NewShell
      title={list ? 'Edit list' : 'New task list'}
      /* The branch the list is filed under, as every New sheet says it (*Alex Kerr · Harrisburg*, Board 19 C). */
      sub={list ? [me.user.name, list.branchName].filter(Boolean).join(' · ') : setup?.sub ?? me.user.name}
      look="task" width={480} phone={phone}
      note="Members see it under Task lists"
      primary={list ? 'Save list' : 'Create list'} phoneAction={list ? 'Save' : 'Create'}
      onPrimary={() => void save()} busy={busy} dirty={false} onClose={onClose} problem={error}
    >
      <div className="flex flex-col">
        <div>
          <div className={label}>Name <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" /></div>
          <input value={name} onChange={(event) => setName(event.target.value)} aria-label="Name"
            className="h-9 w-full rounded-lg border border-line bg-surface px-[11px] text-[13px] font-semibold outline-none focus:border-accent focus:ring-[3px] focus:ring-accent-ring" />
        </div>

        <div className="mt-3">
          <div className={head}>Members</div>
          <div className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border border-line bg-surface px-1.5 py-1">
            {members.map((id) => {
              const person = byId.get(id)
              if (!person) return null
              return (
                <span key={id} className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface pl-[3px] pr-1.5 text-[12px] font-semibold">
                  <Face person={person} size={22} />{person.name}
                  <button type="button" aria-label={`Take ${person.name} off`} className="text-text-muted hover:text-text"
                    onClick={() => setMembers((current) => current.filter((other) => other !== id))}>
                    <Glyph name="close" size={12} />
                  </button>
                </span>
              )
            })}
            {members.length < LIST_MAX_MEMBERS && (
              <button ref={addAt} type="button" onClick={() => setPicking(true)}
                className="inline-flex h-7 items-center gap-1 px-1.5 text-[12px] font-semibold text-accent">
                <Glyph name="plus" size={13} />Add a person
              </button>
            )}
            <span className="ml-auto pr-1 text-[11px] text-text-faint">up to 25</span>
          </div>
        </div>

        <div className="mt-3">
          <div className={head}>Buckets</div>
          <div className="flex flex-col gap-1.5">
            {buckets.map((bucket, index) => (
              <div key={bucket.key}
                ref={(node) => { if (node) rowsAt.current.set(bucket.key, node); else rowsAt.current.delete(bucket.key) }}
                data-bucket-row={bucket.key}
                className={cn('flex h-[38px] items-center gap-2 rounded-lg border bg-surface px-2.5 text-[13px]',
                  lifted === bucket.key ? 'border-accent shadow-[0_6px_18px_rgba(20,20,10,.14)]' : 'border-line')}>
                {/* The grip drags; with the keyboard, ↑ and ↓ move the bucket. */}
                <button type="button" aria-label={`Move ${bucket.name || 'bucket'}`}
                  onPointerDown={(event) => grab(bucket.key, event)}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowUp') { event.preventDefault(); moveTo(bucket.key, index - 1) }
                    if (event.key === 'ArrowDown') { event.preventDefault(); moveTo(bucket.key, index + 1) }
                  }}
                  className={cn('touch-none text-text-faint hover:text-text', lifted === bucket.key ? 'cursor-grabbing' : 'cursor-grab')}>
                  <Glyph name="grip" size={13} />
                </button>
                <input value={bucket.name} aria-label="Bucket"
                  onChange={(event) => setBuckets((current) => current.map((b) => (b.key === bucket.key ? { ...b, name: event.target.value } : b)))}
                  className="min-w-0 flex-1 bg-transparent outline-none" />
                {buckets.length > 1 && (
                  <button type="button" aria-label={`Remove ${bucket.name}`} className="text-text-muted hover:text-text"
                    onClick={() => setBuckets((current) => current.filter((b) => b.key !== bucket.key))}>
                    <Glyph name="close" size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {buckets.length < LIST_MAX_BUCKETS && (
            <button type="button"
              onClick={() => setBuckets((current) => [...current, { key: `new-${Date.now()}`, id: null, name: '' }])}
              className="mt-2 inline-flex items-center gap-[5px] text-[12.5px] font-semibold text-accent">
              <Glyph name="plus" size={13} />Add a bucket
            </button>
          )}
        </div>

        <div className="mt-3">
          <div className={label}>Meets<span className="ml-auto text-text-faint">optional</span></div>
          <MenuSelect
            label="Meets" size="field" noSearch clearable clearLabel="Doesn’t meet"
            value={meetingId}
            options={options.map((m) => ({ value: m.id, label: m.title, sub: m.when, glyph: <Glyph name="calendar" size={14} /> }))}
            placeholder="A meeting"
            onChange={setMeetingId}
            className="w-full"
          />
          <div className="mt-1 text-[11px] leading-[1.4] text-text-faint">
            its next time is offered as a quick due date — Next {(chosenMeeting?.title ?? 'meeting').toLowerCase()}
          </div>
        </div>

        {list?.canEdit && (
          /* Deleting a list takes the list and its buckets, never a task (Aaron, Oct 23). */
          <div className="mt-5 flex items-center gap-3 border-t border-line-soft pt-3.5">
            <span className="min-w-0 flex-1 text-[11.5px] leading-[1.4] text-text-faint">
              Its tasks stay with the people they’re assigned to.
            </span>
            <button type="button" onClick={() => setDeleting(true)} disabled={busy}
              className="inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3 text-[12.5px] font-semibold text-danger hover:bg-hover">
              Delete list
            </button>
          </div>
        )}
      </div>

      <PersonPicker open={picking} onClose={() => setPicking(false)} anchor={addAt.current} people={people}
        taken={members} onPick={(person) => setMembers((current) => [...current, person.id])} />
      {deleting && list && (
        <ConfirmDialog
          title={`Delete ${list.name}?`}
          body="The list and its buckets go. Its tasks stay with the people they’re assigned to, in their tasks."
          confirmLabel="Delete list"
          busyLabel="Deleting…"
          busy={busy}
          layer={(phone ? 60 : SHEET_LAYER) + 5}
          onCancel={() => setDeleting(false)}
          onConfirm={() => void remove()}
        />
      )}
    </NewShell>
  )
}
